import { useEffect, useRef } from 'react';
import { useLocation, useParams, useSearch } from 'wouter';
import { useTranslation } from 'react-i18next';
import { Building2, ExternalLink, Loader2 } from 'lucide-react';
import { getSubdomain } from '@/utils/subdomain';
import BookingWizard from './booking/BookingWizard';
import { BookingProfilePage } from './booking-page/BookingProfilePage';
import { useAdminPreview } from './booking-page/useAdminPreview';
import { BookingPageFrame } from './booking-page/components/BookingPageFrame';
import { usePublicBookingPage } from './booking-page/usePublicBookingPage';
import { useEmbedAccess, useEmbedHeight } from './booking-page/embedRuntime';
import { parseEmbedOptions } from './booking-page/embedOptions';

// How far below the viewport top the content may already sit before a step
// change is left alone. Matches the content's own scroll-mt-4 (16px) plus a
// little slack for sub-pixel layout.
const SETTLED_TOP = 24;

// How long to keep re-issuing the scroll while the step content grows. Step 1
// paints a short skeleton and fills in its slots a fetch later; the browser
// clamps a scroll target to the page height at the time it is issued, so the
// first attempt alone lands short.
const SCROLL_SETTLE_MS = 1200;

// Any of these means the visitor is scrolling themselves; stop correcting.
const TAKEOVER_EVENTS = ['wheel', 'touchstart', 'keydown'] as const;

// The profile page (photos, services, hours) is the landing view. Picking a
// service moves to the booking steps, which track their step in ?step=. Both
// render inside one BookingPageFrame, so the top bar, photos and business
// header stay mounted and only the content below them changes.
function BookingPageRouter({ embedded }: { embedded: boolean }) {
  const params = useParams<{ slug?: string }>();
  const search = useSearch();
  const [location, navigate] = useLocation();
  const { t } = useTranslation('bookingPage');
  const slug = getSubdomain() || params.slug;
  const { organization, services, isLoading, isError } = usePublicBookingPage(slug);
  const access = useEmbedAccess(slug, embedded);
  const previewBlock = useAdminPreview(embedded, search, !!organization);
  useEmbedHeight(embedded);

  const step = parseInt(new URLSearchParams(search).get('step') || '0', 10) || 0;
  const onProfile = step === 0;

  // Moving between the profile and the steps swaps the content under the
  // header. Bring the new content's top into view unless it already sits
  // there: the visitor may have scrolled past its start (down the service
  // list), but on a phone the photo band and business header are ~500px tall,
  // so tapping Book near the top leaves the steps below the fold and the
  // visitor looking at the photos. `content` already carries scroll-mt, and
  // the top bar does not stick, so `block: 'start'` lands correctly.
  const contentRef = useRef<HTMLDivElement>(null);
  const lastStep = useRef(step);
  useEffect(() => {
    if (lastStep.current === step) return;
    lastStep.current = step;
    const content = contentRef.current;
    if (embedded || !content) return;
    const { top } = content.getBoundingClientRect();
    if (top >= 0 && top <= SETTLED_TOP) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = reduceMotion ? 'auto' : 'smooth';
    const scrollToContent = () => content.scrollIntoView({ behavior, block: 'start' });
    scrollToContent();

    // Re-issue while the content grows, so the clamped first attempt is
    // corrected once the real height is known. A touch of the screen means the
    // visitor has taken over, so stop and leave them where they are.
    const observer = new ResizeObserver(() => {
      if (content.getBoundingClientRect().top > SETTLED_TOP) scrollToContent();
    });
    let timer = 0;
    const stop = () => {
      observer.disconnect();
      window.clearTimeout(timer);
      for (const event of TAKEOVER_EVENTS) window.removeEventListener(event, stop);
    };
    observer.observe(content);
    timer = window.setTimeout(stop, SCROLL_SETTLE_MS);
    for (const event of TAKEOVER_EVENTS) window.addEventListener(event, stop, { passive: true });
    return stop;
  }, [step, embedded]);

  if (embedded && access === 'blocked') {
    const directUrl = `${window.location.origin}${window.location.pathname.replace(/\/embed$/, '/book')}`;
    return (
      <div className="flex min-h-[320px] items-center justify-center bg-bg-public p-4" data-testid="embed-blocked">
        <div className="max-w-md rounded-2xl border border-line bg-surface p-8 text-center">
          <h1 className="text-h3 text-ink">{t('embed.blockedTitle')}</h1>
          <p className="mt-2 text-body text-soft">{t('embed.blockedBody')}</p>
          <a href={directUrl} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-body font-medium text-brand-500">
            {t('embed.openPage')}
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    );
  }

  if (isLoading || (embedded && access === 'checking')) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-public">
        <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
      </div>
    );
  }

  if (isError || !organization) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-public p-4">
        <div className="max-w-md rounded-2xl border border-line bg-surface p-8 text-center">
          <Building2 className="mx-auto mb-4 h-12 w-12 text-soft" />
          <h1 className="text-h2 text-ink">{t('errors.notFoundTitle')}</h1>
          <p className="mt-2 text-body text-soft">{t('errors.notFoundBody')}</p>
        </div>
      </div>
    );
  }

  const display = parseEmbedOptions(search, embedded);
  const shown = previewBlock ? { ...organization, booking_page: previewBlock } : organization;
  // Steps 1 and 2 leave room for the fixed mobile bar.
  const withBar = step === 1 || step === 2;
  const bottomPadding = embedded
    ? 'pb-8'
    : onProfile
      ? 'pb-16 sm:pb-20'
      : withBar
        ? 'pb-36 lg:pb-16'
        : 'pb-10 sm:pb-16';

  return (
    <BookingPageFrame organization={shown} display={display} bottomPadding={bottomPadding} contentRef={contentRef}>
      {onProfile ? (
        <BookingProfilePage
          organization={organization}
          shown={shown}
          services={services}
          options={display}
          search={search}
          onNavigate={(nextSearch) => navigate(`${location}${nextSearch}`)}
        />
      ) : (
        <BookingWizard display={display} />
      )}
    </BookingPageFrame>
  );
}

export default function PublicBookingPage() {
  return <BookingPageRouter embedded={false} />;
}

// /embed and /:slug/embed: the same page inside another website's iframe.
export function EmbeddedBookingPage() {
  return <BookingPageRouter embedded />;
}
